import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView, useAnimation, AnimatePresence } from 'motion/react';
import { Lightbulb, TrendingDown, HelpCircle, FileText, EyeOff, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import contact1 from '../../assets/contact1.png';

/* ─────────────────────────────────────────
   Reusable: fade-in when element enters view
───────────────────────────────────────── */
function FadeIn({
  children,
  delay = 0,
  direction = 'up',
  className = '',
}: {
  children: React.ReactNode;
  delay?: number;
  direction?: 'up' | 'down' | 'left' | 'right' | 'none';
  className?: string;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  const offsets = {
    up: { y: 40, x: 0 },
    down: { y: -40, x: 0 },
    left: { y: 0, x: 40 },
    right: { y: 0, x: -40 },
    none: { y: 0, x: 0 },
  };

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, ...offsets[direction] }}
      animate={inView ? { opacity: 1, y: 0, x: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

/* ─────────────────────────────────────────
   Animated quote section:
   3 lines cycle top→middle→bottom (red)
   then settle to default (middle = red)
───────────────────────────────────────── */
function AnimatedMantra({ isId }: { isId: boolean }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  const lines = isId
    ? ['Kedisiplinan mengalahkan prediksi.', 'Data mengalahkan emosi.', 'Sistem mengalahkan sinyal.']
    : ['Discipline beats prediction.', 'Data beats emotion.', 'Systems beat signals.'];

  // phase: 0 = top red, 1 = middle red (default), 2 = bottom red, 3 = settled (middle red)
  const [phase, setPhase] = useState<number>(-1); // -1 = not started

  useEffect(() => {
    if (!inView) return;
    // Sequence: 0 → 2 → 1 (settled)
    const t0 = setTimeout(() => setPhase(0), 200);
    const t1 = setTimeout(() => setPhase(2), 1100);
    const t2 = setTimeout(() => setPhase(1), 2000); // settle: middle red
    return () => {
      clearTimeout(t0);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [inView]);

  const getColor = (index: number) => {
    if (phase === -1) return 'text-[#333333]';
    if (phase === 0 && index === 0) return 'text-[#ff3b3b]';
    if (phase === 1 && index === 1) return 'text-[#ff3b3b]';
    if (phase === 2 && index === 2) return 'text-[#ff3b3b]';
    return 'text-[#333333]';
  };

  const getGlow = (index: number) => {
    if (phase === -1) return '';
    if (phase === 0 && index === 0) return 'drop-shadow-[0_0_40px_rgba(255,59,59,0.5)]';
    if (phase === 1 && index === 1) return 'drop-shadow-[0_0_40px_rgba(255,59,59,0.4)]';
    if (phase === 2 && index === 2) return 'drop-shadow-[0_0_40px_rgba(255,59,59,0.5)]';
    return '';
  };

  return (
    <div ref={ref} className="py-24 text-center relative z-20 w-full mb-24 bg-[#050505]">
      {/* Ambient glow */}
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-[#4a0404] blur-[150px] pointer-events-none"
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: [0, 0.25, 0.15, 0.2] } : {}}
        transition={{ duration: 2.5, times: [0, 0.3, 0.7, 1] }}
      />

      {/* Scanline effect */}
      <motion.div
        className="absolute inset-0 pointer-events-none overflow-hidden"
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 0.03 } : {}}
        transition={{ duration: 1 }}
      >
        {Array.from({ length: 20 }).map((_, i) => (
          <div
            key={i}
            className="absolute w-full h-[1px] bg-white"
            style={{ top: `${i * 5}%` }}
          />
        ))}
      </motion.div>

      <div className="max-w-4xl mx-auto px-6 relative z-10">
        {/* Eyebrow label */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="inline-block px-4 py-1 rounded-full border border-red-900/40 text-[#E26A59] text-[9px] font-bold tracking-widest uppercase mb-10"
        >
          CORE PRINCIPLES
        </motion.div>

        <div className="text-[40px] md:text-[60px] font-bold tracking-tighter leading-[1.2]">
          {lines.map((line, i) => (
            <motion.div
              key={i}
              className={`mb-2 transition-colors duration-500 ${getColor(i)} ${getGlow(i)}`}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
            >
              {line}
            </motion.div>
          ))}
        </div>

        {/* Bottom decoration */}
        <motion.div
          className="flex items-center justify-center gap-3 mt-12"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 1.2 }}
        >
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className={`h-[2px] rounded-full ${i === 1 ? 'w-12 bg-[#E26A59]' : 'w-4 bg-[#333]'}`}
              animate={phase >= 1 ? { scaleX: [1, 1.3, 1] } : {}}
              transition={{ duration: 0.4, delay: i * 0.1 }}
            />
          ))}
        </motion.div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   Glowing dot for timeline
───────────────────────────────────────── */
function TimelineDot({ inView }: { inView: boolean }) {
  return (
    <motion.div
      className="absolute left-1/2 -translate-x-1/2 w-[11px] h-[11px] bg-[#E26A59] rounded-full hidden md:block"
      initial={{ scale: 0, opacity: 0 }}
      animate={inView ? { scale: 1, opacity: 1 } : {}}
      transition={{ duration: 0.4, ease: 'backOut' }}
    >
      <motion.div
        className="absolute inset-0 rounded-full bg-[#E26A59]"
        animate={inView ? { scale: [1, 2.5, 1], opacity: [0.8, 0, 0.8] } : {}}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      />
    </motion.div>
  );
}

/* ─────────────────────────────────────────
   Stat card with count-up feel
───────────────────────────────────────── */
function StatCard({ value, label, delay = 0 }: { value: string; label: string; delay?: number }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });

  return (
    <motion.div
      ref={ref}
      className="bg-[#1A0505] border border-[#2F0B0B] rounded-xl p-5 flex-1 shadow-lg overflow-hidden relative"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={inView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      <motion.div
        className="absolute inset-0 bg-[#E26A59]/5"
        animate={inView ? { opacity: [0, 1, 0] } : {}}
        transition={{ duration: 0.8, delay }}
      />
      <div className="text-[#E26A59] font-bold text-2xl mb-1 tracking-tight relative z-10">{value}</div>
      <div className="text-[#8C8C8C] text-[10px] font-bold tracking-widest uppercase relative z-10">{label}</div>
    </motion.div>
  );
}

/* ─────────────────────────────────────────
   Small info card with hover glow
───────────────────────────────────────── */
function InfoCard({
  icon: Icon,
  title,
  desc,
  delay = 0,
}: {
  icon: React.ElementType;
  title: string;
  desc: string;
  delay?: number;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });

  return (
    <motion.div
      ref={ref}
      className="bg-[#0A0A0C] border border-white/5 rounded-2xl p-6 text-left group relative overflow-hidden"
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ borderColor: 'rgba(255,255,255,0.1)', y: -2 }}
    >
      {/* Hover glow */}
      <motion.div
        className="absolute inset-0 bg-[#E26A59]/0 group-hover:bg-[#E26A59]/[0.03] transition-all duration-500 rounded-2xl pointer-events-none"
      />
      <Icon className="w-5 h-5 text-[#E26A59] mb-4 relative z-10" />
      <h4 className="text-white font-bold text-[14px] mb-2 relative z-10">{title}</h4>
      <p className="text-[#8C8C8C] text-[11px] leading-relaxed relative z-10">{desc}</p>
    </motion.div>
  );
}

/* ─────────────────────────────────────────
   Main Component
───────────────────────────────────────── */
export default function AboutUs() {
  const { t, i18n } = useTranslation();
  const isId = i18n.language === 'id';
  const navigate = useNavigate();

  // Refs for timeline dot visibility
  const block1Ref = useRef(null);
  const block2Ref = useRef(null);
  const block3Ref = useRef(null);
  const block1InView = useInView(block1Ref, { once: true, margin: '-100px' });
  const block2InView = useInView(block2Ref, { once: true, margin: '-100px' });
  const block3InView = useInView(block3Ref, { once: true, margin: '-100px' });

  return (
    <div className="bg-[#050505] text-white pt-32 pb-0 relative overflow-hidden flex flex-col min-h-screen">
      <div className="max-w-[1200px] w-full mx-auto px-6 relative z-10 flex-1">

        {/* ── Header ── */}
        <div className="text-center mb-32 relative z-10">
          <FadeIn delay={0}>
            <div className="inline-block px-5 py-1.5 rounded-full border border-red-900/50 text-[#E26A59] text-[10px] font-bold tracking-widest uppercase mb-10 mt-10">
              {t('about.badge')}
            </div>
          </FadeIn>

          <FadeIn delay={0.1}>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
              {isId ? 'Dari Kekacauan' : 'From Chaos'}{' '}
              {isId ? 'menjadi' : 'to'}{' '}
              <motion.span
                className="text-[#E26A59] inline-block"
                animate={{ textShadow: ['0 0 20px rgba(226,106,89,0)', '0 0 40px rgba(226,106,89,0.5)', '0 0 20px rgba(226,106,89,0)'] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              >
                {isId ? 'Kejelasan.' : 'Clarity.'}
              </motion.span>
            </h1>
          </FadeIn>

          <FadeIn delay={0.2}>
            <p className="text-[#8C8C8C] text-lg max-w-2xl mx-auto leading-relaxed">
              {isId
                ? 'Evolusi platform kecerdasan trading utama di Indonesia.'
                : "The evolution of Indonesia's premier trading intelligence platform."}
              <br />
              {isId
                ? 'Dibuat oleh trader, untuk trader yang mengandalkan data, bukan sekadar keberuntungan.'
                : 'Built by traders, for traders who demand more than luck.'}
            </p>
          </FadeIn>

          {/* Floating particles decoration */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-[2px] h-[2px] rounded-full bg-[#E26A59]/40"
                style={{
                  left: `${15 + i * 14}%`,
                  top: `${20 + (i % 3) * 25}%`,
                }}
                animate={{
                  y: [-8, 8, -8],
                  opacity: [0.2, 0.6, 0.2],
                }}
                transition={{
                  duration: 3 + i * 0.4,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: i * 0.5,
                }}
              />
            ))}
          </div>
        </div>

        <div className="relative">
          {/* Vertical timeline line */}
          <motion.div
            className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-[1px] md:block hidden"
            style={{
              background: 'linear-gradient(to bottom, transparent, #E26A59 10%, #E26A59 90%, transparent)',
            }}
            initial={{ scaleY: 0, transformOrigin: 'top' }}
            whileInView={{ scaleY: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          />

          {/* ── Block 1: 2026 The Realization ── */}
          <div ref={block1Ref} className="flex flex-col md:flex-row items-center justify-between mb-40 relative">
            <div className="absolute left-1/2 -translate-x-1/2 mt-8">
              <TimelineDot inView={block1InView} />
            </div>

            {/* Left: Text */}
            <FadeIn delay={0.1} direction="right" className="w-full md:w-[45%] md:pr-12 lg:pr-20 text-center md:text-right flex flex-col items-center md:items-end z-10">
              <motion.div
                className="text-[55px] font-bold text-[#E26A59] mb-0 tracking-tighter leading-none"
                animate={{ textShadow: ['0 0 30px rgba(226,106,89,0.3)', '0 0 60px rgba(226,106,89,0.6)', '0 0 30px rgba(226,106,89,0.3)'] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              >
                2026
              </motion.div>
              <h3 className="text-[32px] font-bold mb-6">{isId ? 'Sebuah Kesadaran' : 'The Realization'}</h3>
              <p className="text-[#8C8C8C] text-[15px] leading-relaxed mb-10 w-full max-w-md">
                {isId
                  ? 'Enrico, Naomi, dan Erica menyaksikan kehancuran pasar ritel yang didorong oleh sinyal Telegram dan FOMO buta. Mereka menyadari, jembatan antara perjudian dan trading adalah data.'
                  : 'Enrico, Naomi, and Erica witness the carnage of a retail market fueled by Telegram signals and blind FOMO. They realize the bridge between gambling and trading is data.'}
              </p>

              {/* Quote Card */}
              <motion.div
                className="bg-white rounded-[20px] p-6 text-left w-full max-w-sm relative flex gap-4 items-start text-[#050505] shadow-2xl"
                initial={{ opacity: 0, y: 20, rotate: -1 }}
                whileInView={{ opacity: 1, y: 0, rotate: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -4 }}
              >
                <div className="bg-[#FFF0F0] p-2 rounded-full flex-shrink-0 mt-1">
                  <Lightbulb className="w-5 h-5 text-[#E26A59]" />
                </div>
                <p className="text-[13px] font-semibold opacity-70 leading-relaxed italic">
                  {isId
                    ? '"Mayoritas trader kalah bukan karena sinyal yang buruk — mereka kalah karena kurangnya disiplin dan kesadaran diri."'
                    : '"Most traders don\'t lose because of bad signals — they lose because they lack discipline and self-awareness."'}
                </p>
              </motion.div>
            </FadeIn>

            {/* Right: Image */}
            <FadeIn delay={0.2} direction="left" className="w-full md:w-[45%] md:pl-12 lg:pl-20 mt-16 md:mt-0 z-10">
              <motion.div
                className="relative bg-[#1A0505] border-2 border-[#E26A59]/50 rounded-3xl overflow-hidden aspect-[4/3] group shadow-[0_0_30px_rgba(226,106,89,0.4),0_0_60px_rgba(226,106,89,0.2)]"
                whileHover={{
                  boxShadow: '0 0 50px rgba(226,106,89,0.7), 0 0 100px rgba(226,106,89,0.3)',
                  borderColor: 'rgba(226,106,89,0.8)',
                }}
                transition={{ duration: 0.3 }}
              >
                <img src={contact1} alt="The Founders" className="w-full h-full object-cover" />
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#E26A59] rounded-full blur-[80px] opacity-20 pointer-events-none" />

                {/* Scan line animation */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-b from-transparent via-[#E26A59]/5 to-transparent pointer-events-none"
                  animate={{ y: ['-100%', '200%'] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'linear', repeatDelay: 2 }}
                />

                <div className="absolute bottom-6 left-6">
                  <div className="text-white font-bold text-lg mb-0.5">{isId ? 'Para Pendiri' : 'The Founders'}</div>
                  <div className="text-[#8C8C8C] text-xs font-medium tracking-wide">
                    {isId ? 'Sesi diskusi larut malam di Jakarta' : 'Late-night sessions in Jakarta'}
                  </div>
                </div>
              </motion.div>
            </FadeIn>
          </div>

          {/* ── Block 2: 90% The Market Reality ── */}
          <div ref={block2Ref} className="flex flex-col md:flex-row items-center justify-between mb-32 relative">
            <div className="absolute left-1/2 -translate-x-1/2 mt-8">
              <TimelineDot inView={block2InView} />
            </div>

            {/* Left: Cards Grid */}
            <div className="w-full md:w-[45%] md:pr-12 lg:pr-20 mt-16 md:mt-0 order-2 md:order-1 z-10">
              <div className="grid grid-cols-2 gap-4">
                <InfoCard
                  icon={TrendingDown}
                  title={isId ? 'Sinyal' : 'Signals'}
                  desc={isId ? 'Mengikuti kerumunan membabi buta, tanpa personal edge.' : 'Blindly following the crowd without personal edge.'}
                  delay={0.1}
                />
                <InfoCard
                  icon={HelpCircle}
                  title={isId ? 'Emosi' : 'Emotion'}
                  desc={isId ? 'FOMO di harga pucuk, lanjut panic selling di harga mutlak terendah.' : 'FOMO at the top, panic selling at the absolute bottom.'}
                  delay={0.2}
                />
                <InfoCard
                  icon={FileText}
                  title={isId ? 'Pajak' : 'Tax'}
                  desc={isId ? 'Tenggelam dalam lautan ekspor CSV saat musim pelaporan.' : 'Drowning in CSV exports during reporting season.'}
                  delay={0.3}
                />
                <InfoCard
                  icon={EyeOff}
                  title={isId ? 'Tak Jelas' : 'Opaque'}
                  desc={isId ? 'Tidak ada kejelasan tentang kinerja portofolio yang sebenarnya.' : 'No clarity on actual portfolio performance.'}
                  delay={0.4}
                />
              </div>
            </div>

            {/* Right: Text */}
            <FadeIn delay={0.1} direction="left" className="w-full md:w-[45%] md:pl-12 lg:pl-20 order-1 md:order-2 text-center md:text-left z-10 flex flex-col items-center md:items-start">
              <motion.div
                className="text-[55px] font-bold text-[#E26A59] mb-0 tracking-tighter leading-none"
                animate={{ textShadow: ['0 0 30px rgba(226,106,89,0.3)', '0 0 60px rgba(226,106,89,0.6)', '0 0 30px rgba(226,106,89,0.3)'] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
              >
                90%+
              </motion.div>
              <h3 className="text-[32px] font-bold mb-6">{isId ? 'Realita Pasar' : 'The Market Reality'}</h3>
              <p className="text-[#8C8C8C] text-[15px] leading-relaxed mb-10 w-full max-w-md">
                {isId
                  ? 'Data menunjukkan bahwa 90% trader ritel gagal dalam tahun pertama mereka. Alasannya sederhana: Tidak ada dokumentasi, tidak ada akuntabilitas, dan tidak ada sistem yang dapat diulang (repeatable).'
                  : 'Data shows that 90% of retail traders fail within their first year. The reason is simple: No documentation, no accountability, no repeatable system.'}
              </p>
              <div className="flex gap-4 w-full max-w-md">
                <StatCard value="3x" label={isId ? 'Pengali Sukses' : 'Success Multiplier'} delay={0.3} />
                <StatCard value="67%" label={isId ? 'Hemat Waktu' : 'Time Saved'} delay={0.45} />
              </div>
            </FadeIn>
          </div>
        </div>
      </div>

      {/* ── Animated Mantra Section ── */}
      <AnimatedMantra isId={isId} />

      <div className="max-w-[1200px] w-full mx-auto px-6 relative z-10 flex-1">
        <div className="relative">
          {/* Line continues */}
          <motion.div
            className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-[1px] md:block hidden"
            style={{
              background: 'linear-gradient(to bottom, #E26A59 0%, #E26A59 90%, transparent)',
            }}
            initial={{ scaleY: 0, transformOrigin: 'top' }}
            whileInView={{ scaleY: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          />

          {/* ── Block 3: The Software Layer ── */}
          <div ref={block3Ref} className="flex flex-col md:flex-row items-center justify-between mb-32 relative">
            <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2">
              <TimelineDot inView={block3InView} />
            </div>

            {/* Left: Trust Card */}
            <FadeIn delay={0.1} direction="right" className="w-full md:w-[45%] md:pr-12 lg:pr-20 text-left z-10">
              <motion.div
                className="bg-[#0A0A0C] border border-white/5 rounded-[24px] p-8 md:p-10 relative overflow-hidden group shadow-2xl"
                whileHover={{ borderColor: 'rgba(255,255,255,0.1)', y: -4 }}
                transition={{ duration: 0.3 }}
              >
                {/* Corner glow */}
                <motion.div
                  className="absolute top-0 right-0 w-48 h-48 bg-[#E26A59] rounded-full blur-[100px] opacity-0 group-hover:opacity-10 transition-opacity duration-500 pointer-events-none"
                />

                <div className="flex items-center gap-4 mb-6 relative z-10">
                  <motion.div
                    className="w-12 h-12 rounded-full bg-[#1A0505] border border-[#2F0B0B] flex items-center justify-center flex-shrink-0"
                    animate={{ boxShadow: ['0 0 0px rgba(226,106,89,0)', '0 0 20px rgba(226,106,89,0.4)', '0 0 0px rgba(226,106,89,0)'] }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    <ShieldCheck className="w-5 h-5 text-[#E26A59]" />
                  </motion.div>
                  <h4 className="text-white font-bold text-[18px]">
                    {isId ? 'Jaminan Non-Kustodian' : 'Non-Custodial Trust'}
                  </h4>
                </div>

                <p className="text-[#8C8C8C] text-[14px] leading-relaxed mb-8 relative z-10">
                  {isId
                    ? 'Kami membangun Catat Crypto di atas landasan "Hanya Baca (Read-Only)". Kami tidak pernah menyentuh dana Anda, tidak pernah mengeksekusi trading, dan tidak akan pernah menjual data Anda. Kami hanyalah lensa yang membantu Anda melihat pencapaian dan mengawal kesuksesan Anda.'
                    : 'We built Catat Crypto on a "Read-Only" foundation. We never touch your funds, never execute trades, and never sell your data. We are simply the lens through which you see your success.'}
                </p>

                <div className="flex flex-wrap items-center gap-3 text-[9px] font-bold tracking-[0.1em] text-[#666666] uppercase relative z-10">
                  <span>SOC 2 / ISO 27001 READY</span>
                  <div className="w-[3px] h-[3px] rounded-full bg-[#333333]" />
                  <span>PRIVACY FIRST</span>
                  <div className="w-[3px] h-[3px] rounded-full bg-[#333333]" />
                  <span>NO KEYS STORED</span>
                </div>
              </motion.div>
            </FadeIn>

            {/* Right: Text + Features */}
            <FadeIn delay={0.2} direction="left" className="w-full md:w-[45%] md:pl-12 lg:pl-20 mt-16 md:mt-0 text-left z-10 flex flex-col items-start">
              <h3 className="text-4xl md:text-[50px] font-bold mb-8 leading-[1.1] tracking-tight">
                {isId ? 'Kami Menyediakan Alat' : 'The Software Layer'}
                <br />
                <span className="text-[#E26A59]">{isId ? 'Yang Berubah Jadi Pengalaman.' : 'that changes'}</span>
                <br />
                {isId ? '' : 'everything.'}
              </h3>
              <ul className="space-y-4">
                {(isId
                  ? ['Pencatatan Jurnal Otomatis', 'Pengenalan Pola Algoritma', 'Pelaporan Pajak Terpadu']
                  : ['Automated Journaling', 'Pattern Recognition', 'Unified Tax Reporting']
                ).map((item, i) => (
                  <motion.li
                    key={i}
                    className="flex items-center gap-3 text-lg font-medium text-white/90"
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: '-40px' }}
                    transition={{ duration: 0.5, delay: 0.3 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <motion.div
                      className="w-5 h-5 rounded-full bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0"
                      animate={{ boxShadow: ['0 0 0 rgba(52,211,153,0)', '0 0 10px rgba(52,211,153,0.3)', '0 0 0 rgba(52,211,153,0)'] }}
                      transition={{ duration: 2, repeat: Infinity, delay: i * 0.6 }}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#34D399]" />
                    </motion.div>
                    {item}
                  </motion.li>
                ))}
              </ul>
            </FadeIn>
          </div>
        </div>

        {/* ── Mission Section ── */}
        <div className="text-center relative z-10 pt-16 pb-32">
          <FadeIn delay={0}>
            <h2 className="text-[32px] md:text-[40px] font-bold text-[#E26A59] mb-8 tracking-tight">
              {t('about.mission')}
            </h2>
          </FadeIn>

          <FadeIn delay={0.1}>
            <p className="text-[30px] md:text-[42px] font-bold text-white max-w-[800px] mx-auto leading-[1.2] mb-16 tracking-tight">
              {t('about.missionDesc')}
            </p>
          </FadeIn>

          <FadeIn delay={0.2}>
            <div className="w-24 h-[1px] bg-[#331111] mx-auto mb-16" />
          </FadeIn>

          <FadeIn delay={0.3}>
            <p className="text-2xl text-[#8C8C8C] italic mb-12">"Stop guessing. Start measuring."</p>
          </FadeIn>

          <FadeIn delay={0.4}>
            <motion.button
              onClick={() => navigate('/register')}
              className="px-10 py-5 bg-[#FF2020] text-white rounded-xl font-bold text-lg relative overflow-hidden"
              whileHover={{ scale: 1.04, boxShadow: '0 0 60px rgba(255,32,32,0.7)' }}
              whileTap={{ scale: 0.97 }}
              animate={{ boxShadow: ['0 0 40px rgba(255,32,32,0.4)', '0 0 60px rgba(255,32,32,0.6)', '0 0 40px rgba(255,32,32,0.4)'] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
            >
              {/* Shimmer effect */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-12"
                animate={{ x: ['-200%', '200%'] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: 'linear', repeatDelay: 1.5 }}
              />
              <span className="relative z-10">Join the Revolution</span>
            </motion.button>
          </FadeIn>
        </div>
      </div>
    </div>
  );
}
